import React, { useEffect, useMemo } from 'react'
import {
  Button,
  Card,
  Input,
  ModuleTitle,
} from '../../../../components_deprecated'
import { FleetFilter } from './FleetFilter'
import { FleetTable } from './FleetTable'
import { useAdmin, useBreadcrumbs, useCompany } from '../../../../utils'
import { useInfoModal } from '../../../../utils/hooks/useInfoModal/useInfoModal'
import { PageType } from '../../../common/consts'
import type { CreateVehicleByRegNrModalResolve, VehicleFilter } from '../../types'
import { CreateVehicleByRegNrModal } from './CreateVehicleByRegNrModal'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useQuery } from '@apollo/client'
import type {
  AllVehiclesQuery,
  AllVehiclesQueryVariables,
} from '../../types.graphql'
import { ALL_VEHICLES_QUERY } from '../../queries'
import uniq from 'lodash/uniq'
import { usePrompt } from '@ur-solutions/react-components'
import {
  useEquipmentExportMutations,
  useVehicleMutations,
} from '../mutations.hooks'
import styled from 'styled-components'
import { isMobileOnly } from 'react-device-detect'
import { ExportEquipmentModal } from './ExportEquipmentModal'
import { ExportFormat, ExportOption } from '../../../exports'

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;

  ${isMobileOnly &&
  `
    grid-auto-flow: row;
  `}
`

const ExportButton = styled(Button)`
  margin-left: auto;
  padding: 0 2rem;
  font-weight: 500;

  ${isMobileOnly &&
  `
    margin-left: 0;
    order: -1;
    width: 100%;
  `}
`

type StyledBadgeProps = {
  active: boolean
}

const StyledBadge = styled.div<StyledBadgeProps>`
  display: flex;
  height: fit-content;
  background: ${props =>
    props.active ? props.theme.colors.secondary : props.theme.colors.gray8};
  padding: 0.3rem 0.8rem;
  width: fit-content;
  font-size: 12px;
  border-radius: ${props => props.theme.standardBorderRadius};
  color: ${props =>
    props.active ? props.theme.colors.white : props.theme.colors.black};
`

const ContainerBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  row-gap: 0.5rem;

  & > h4 {
    margin: 0 1rem 0 0;
  }
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ContainerStyled = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 1rem;

  ${isMobileOnly &&
  `
    flex-direction: column;
    align-items: flex-start;
  `}
`

const DEFAULT = true

const EquipmentTab = () => {
  const translations = useTranslate({
    equipment: 'common.equipment',
    addVehicle: 'vehicles.add-vehicle',
    export: 'common.export',
    owned: 'vehicles.owned',
    type: 'common.type',
    unowned: 'vehicles.unowned',
  })
  const isAdmin = useAdmin()
  const addPrompt = usePrompt()
  const { mutations } = useVehicleMutations()
  const { setOverrides, overrides } = useBreadcrumbs()
  const { InfoModal, infoContent, InfoButton } = useInfoModal(PageType.VEHICLES)
  const company = useCompany()

  /* CreateModal */
  async function handleCreateByRegNrVehicle() {
    const { data } = await addPrompt<CreateVehicleByRegNrModalResolve | null>(
      resolve => <CreateVehicleByRegNrModal onSubmit={resolve} />
    )
    if (!data) return
    const { form } = data
    await mutations.createByRegNr({
      variables: {
        input: form,
      },
    })
  }

  /* Forms */

  const {
    formValues: filter,
    formChangeHandler: handler,
    updateForm,
  } = useForm<VehicleFilter>({
    values: {
      query: undefined,
      owned: true,
      orderBy: 'registrationNumber',
      includedTypes: [],
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: 'firmadok__fleet-overview-filter',
        transformValues: {},
      },
    },
  })
  const { data, loading } = useQuery<
    AllVehiclesQuery,
    AllVehiclesQueryVariables
  >(ALL_VEHICLES_QUERY, {
    variables: {
      q: filter.query,
      currentlyOwned: filter.owned,
      orderBy: filter.orderBy,
    },
  })

  const vehicles = useMemo(
    () => data?.allVehicles.edges.map(edge => edge.node) ?? [],
    [data]
  )
  const vehicleTypes = useMemo(
    () =>
      uniq(vehicles.map(vehicle => vehicle.vehicleType)).filter(
        type => type !== null
      ),
    [vehicles]
  )

  const includedVehicles =
    filter.includedTypes.length === 0
      ? vehicles
      : vehicles.filter(v => filter.includedTypes.includes(v.vehicleType))

  useEffect(() => {
    if (overrides['vehicle-fleet']) return
    setOverrides({ 'vehicle-fleet': translations.equipment })
  }, [overrides, setOverrides, translations.equipment])

  const { exportEquipments } = useEquipmentExportMutations()

  async function doExport(values: { exportFormat: ExportFormat }) {
    await exportEquipments({
      variables: {
        exportType: 'vehicles',
        company: company.id,
        exportFormat: values.exportFormat,
        exportOption:
          values.exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
      },
    })
  }

  async function handleExport() {
    const { data } = await addPrompt<{ exportFormat: ExportFormat } | null>(
      resolve => <ExportEquipmentModal onSubmit={resolve} />
    )
    if (!data) return

    await doExport(data)
  }

  return (
    <>
      <ModuleTitle>
        <ContainerStyled>
          {translations.equipment}
          {infoContent && <InfoButton />}
          {isAdmin && (
            <ExportButton
              iconLeftProps={{ icon: 'download' }}
              fullWidth={isMobileOnly}
              disabled={loading}
              onClick={handleExport}
            >
              <span>{translations.export}</span>
            </ExportButton>
          )}
        </ContainerStyled>
      </ModuleTitle>

      <Controls>
        <Input
          value={filter.query ?? ''}
          type="search"
          fullWidth
          onChange={handler('query')}
        />

        <FleetFilter
          filter={filter}
          vehicleTypes={vehicleTypes}
          onUpdate={updateForm}
        />

        <Button
          iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
          color="primary"
          onClick={handleCreateByRegNrVehicle}
        >
          {translations.addVehicle}
        </Button>
      </Controls>
      {(filter.owned !== DEFAULT ||
        (vehicleTypes?.length > 0 && filter.includedTypes.length > 0)) && (
        <Controls>
          <RowContainer>
            <ContainerBadge>
              <h4>{translations.owned}</h4>
              <StyledBadge active={true}>
                {filter.owned ? translations.owned : translations.unowned}
              </StyledBadge>
            </ContainerBadge>
            {vehicleTypes?.length > 0 && filter.includedTypes.length > 0 && (
              <ContainerBadge>
                <h4>{translations.type}</h4>
                {vehicleTypes.map(vehicle => {
                  const checked =
                    filter.includedTypes.length > 0 &&
                    filter.includedTypes.includes(vehicle)
                  if (!checked) return null
                  return (
                    <StyledBadge key={vehicle} active={true}>
                      {vehicle}
                    </StyledBadge>
                  )
                })}
              </ContainerBadge>
            )}
          </RowContainer>
        </Controls>
      )}
      <FleetTable
        vehicles={includedVehicles}
        filter={filter}
        onSort={handler('orderBy')}
      />
      {infoContent && <InfoModal />}
    </>
  )
}

export default EquipmentTab
